import { createAction, handleActions } from 'redux-actions';
import { TraceStatus } from '@redux/types';

export const initialState = {
  isLoading: true,
  isDisabled: false,
  isAlertOpen: false,
  isConfirmOpen: false,
  user: null,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
};

// Actions

export const USER_DETAILS_FETCH_STARTED = 'userDetails/USER_DETAILS_FETCH_STARTED';
export const USER_DETAILS_FETCH_SUCCEEDED = 'userDetails/USER_DETAILS_FETCH_SUCCEEDED';
export const USER_EMAIL_SET = 'userDetails/USER_EMAIL_SET';
export const USER_API_TOKEN_SET = 'userDetails/USER_TOKEN_SET';
export const RESET_STATE = 'userDetails/RESET_STATE';
export const UPDATE_USER = 'userDetails/UPDATE_USER_STARTED';
export const UPDATE_USER_FAILED = 'userDetails/UPDATE_USER_FAILED';
export const UPDATE_USER_SUCCEEDED = 'userDetails/UPDATE_USER_SUCCEEDED';
export const CLOSE_ALERT = 'userDetails/CLOSE_ALERT';
export const SHOW_CONFIRM = 'userDetails/SHOW_CONFIRM';
export const CLOSE_CONFIRM = 'userDetails/CLOSE_CONFIRM';
export const DELETE_USER = 'userDetails/DELETE_USER_STARTED';
export const DELETE_USER_SUCCEEDED = 'userDetails/DELETE_USER_SUCCEEDED';
export const DELETE_USER_FAILED = 'userDetails/DELETE_USER_FAILED';

// Action creators

export const fetchUserDetails = createAction(USER_DETAILS_FETCH_STARTED);
export const fetchUserDetailsSucceeded = createAction(USER_DETAILS_FETCH_SUCCEEDED);
export const setUserEmail = createAction(USER_EMAIL_SET);
export const setUserApiToken = createAction(USER_API_TOKEN_SET);
export const resetState = createAction(RESET_STATE);
export const updateUser = createAction(UPDATE_USER);
export const updateUserFailed = createAction(UPDATE_USER_FAILED);
export const updateUserSucceeded = createAction(UPDATE_USER_SUCCEEDED);
export const closeAlert = createAction(CLOSE_ALERT);
export const showConfirm = createAction(SHOW_CONFIRM);
export const closeConfirm = createAction(CLOSE_CONFIRM);
export const deleteUser = createAction(DELETE_USER);
export const deleteUserSucceeded = createAction(DELETE_USER_SUCCEEDED);
export const deleteUserFailed = createAction(DELETE_USER_FAILED);

// Reducer

const handleFetchStarted = state => ({
  ...state,
  isLoading: true,
});

const handleFetchSucceeded = (state, { payload }) => ({
  ...state,
  isLoading: false,
  user: payload,
});

const handleSetUserEmail = (state, { payload }) => ({
  ...state,
  user: {
    ...state.user,
    unconfirmed_email: payload.email,
  },
});

const handleSetUserApiToken = (state, { payload }) => ({
  ...state,
  user: {
    ...state.user,
    authentication_token: payload.authentication_token,
  },
});

const handleResetState = () => ({
  ...initialState,
});

const handleUpdateUserStarted = state => ({
  ...state,
  isDisabled: true,
});

const handleUpdateUserFailed = (state, { payload }) => ({
  ...state,
  isDisabled: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: payload,
  },
});

const handleUpdateUserSucceeded = state => ({
  ...state,
  isDisabled: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('users.user-update-success.message'),
  },
});

const handleCloseAlert = state => ({
  ...state,
  isAlertOpen: false,
});

const handleShowConfirm = state => ({
  ...state,
  isConfirmOpen: true,
});

const handleCloseConfirm = state => ({
  ...state,
  isConfirmOpen: false,
});

const handleDeleteUserStarted = state => ({
  ...state,
  isConfirmOpen: false,
});

const handleDeleteUserFailed = (state, { payload }) => ({
  ...state,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: payload,
  },
});

const handleDeleteUserSucceeded = state => ({
  ...state,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('users.user-delete-success.message'),
  },
});

export default handleActions(
  {
    [USER_DETAILS_FETCH_STARTED]: handleFetchStarted,
    [USER_DETAILS_FETCH_SUCCEEDED]: handleFetchSucceeded,
    [USER_EMAIL_SET]: handleSetUserEmail,
    [USER_API_TOKEN_SET]: handleSetUserApiToken,
    [RESET_STATE]: handleResetState,
    [UPDATE_USER]: handleUpdateUserStarted,
    [UPDATE_USER_FAILED]: handleUpdateUserFailed,
    [UPDATE_USER_SUCCEEDED]: handleUpdateUserSucceeded,
    [CLOSE_ALERT]: handleCloseAlert,
    [SHOW_CONFIRM]: handleShowConfirm,
    [CLOSE_CONFIRM]: handleCloseConfirm,
    [DELETE_USER]: handleDeleteUserStarted,
    [DELETE_USER_FAILED]: handleDeleteUserFailed,
    [DELETE_USER_SUCCEEDED]: handleDeleteUserSucceeded,
  },
  initialState,
);

// Selectors

export const getUserDetailsState = state => state.userDetails.userDetails;

export const getUserDetails = state => state.userDetails.userDetails.user;

export const isUserDetailsLoading = state => state.userDetails.userDetails.isLoading;

export const isAccountOwner = state => {
  const userDetails = getUserDetails(state);
  const currentUser = state.global.user;

  if (!userDetails || !currentUser) {
    return false;
  }

  return userDetails.id === currentUser.id;
};
